'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { isValidPassword } from '@paction-2.0/util/validation';
import { useAuth } from '@/lib/auth/AuthProvider';

interface CompletionContext {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  requiresPhoneVerification: boolean;
  accountId: number | null;
  isNewSignup: boolean;
}

const parseApiError = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json();
    if (payload?.message) return String(payload.message);
    if (payload?.error) return String(payload.error);
  } catch (_) {
    // fallthrough
  }
  return response.statusText || 'Request failed';
};

const fetchContext = async (email: string): Promise<CompletionContext> => {
  const response = await fetch('/api/v1.0/public/complete-signup/context', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!response.ok) throw new Error(await parseApiError(response));
  const payload = await response.json();
  if (payload.success === false)
    throw new Error(payload.error || 'Failed to load account');
  return payload.data;
};

const requestVerificationCode = async (
  email: string,
  phone: string
): Promise<{ status: string; sid: string | null }> => {
  const response = await fetch(
    '/api/v1.0/public/complete-signup/request-code',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ email, phone })
    }
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    if (
      payload?.code === 'VERIFICATION_UNAVAILABLE' ||
      payload?.error?.includes('temporarily unavailable')
    ) {
      throw new Error('temporarily unavailable');
    }
    throw new Error(
      payload?.message ??
        payload?.error ??
        response.statusText ??
        'Request failed'
    );
  }
  const payload = await response.json();
  if (payload.success === false)
    throw new Error(payload.error || 'Failed to send code');
  return payload.data;
};

const finishSignup = async (
  email: string,
  phone: string,
  code: string | undefined,
  password: string,
  firstName?: string,
  lastName?: string,
  completionSource?: string
): Promise<void> => {
  const response = await fetch('/api/v1.0/public/complete-signup/finish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email,
      phone,
      ...(code ? { code } : {}),
      password,
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(completionSource ? { completionSource } : {})
    })
  });
  if (!response.ok) throw new Error(await parseApiError(response));
  const payload = await response.json();
  if (payload.success === false)
    throw new Error(payload.error || 'Failed to complete signup');
};

const CODE_LENGTH = 6;

export interface UseInlineAccountCompletionProps {
  email: string;
  redirectPath: string;
  onComplete: (redirect: string) => void;
  initialPhone?: string;
  completionSource?: string;
}

export function useInlineAccountCompletion({
  email,
  redirectPath,
  onComplete,
  initialPhone,
  completionSource
}: UseInlineAccountCompletionProps) {
  const { refreshSession } = useAuth();

  const [context, setContext] = useState<CompletionContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(true);

  const [phone, setPhone] = useState(initialPhone ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');

  const [sendingCode, setSendingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeMessage, setCodeMessage] = useState<string | null>(null);
  const [codeRequested, setCodeRequested] = useState(false);
  const [verificationSkipped, setVerificationSkipped] = useState(false);

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    setLoadingContext(true);
    fetchContext(email)
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        if (ctx.phone && !initialPhone) setPhone(ctx.phone);
        if (ctx.firstName) setFirstNameInput(ctx.firstName);
        if (ctx.lastName) setLastNameInput(ctx.lastName);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load account');
      })
      .finally(() => {
        if (!cancelled) setLoadingContext(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, initialPhone]);

  const passwordMismatch = useMemo(
    () => confirmPassword.length > 0 && password !== confirmPassword,
    [password, confirmPassword]
  );

  const canSendCode = useMemo(
    () => phone.replace(/\D/g, '').length >= 10 && !sendingCode,
    [phone, sendingCode]
  );

  const canSubmit = useMemo(() => {
    if (submitting || loadingContext) return false;
    if (!isValidPassword(password)) return false;
    if (passwordMismatch) return false;
    if (
      context?.isNewSignup &&
      (!firstNameInput.trim() || !lastNameInput.trim())
    )
      return false;
    if (!verificationSkipped && codeRequested && code.length < CODE_LENGTH)
      return false;
    return true;
  }, [
    submitting,
    loadingContext,
    password,
    passwordMismatch,
    context?.isNewSignup,
    firstNameInput,
    lastNameInput,
    verificationSkipped,
    codeRequested,
    code
  ]);

  const handleSendCode = useCallback(async () => {
    if (!canSendCode) return;
    setSendingCode(true);
    setError(null);
    try {
      await requestVerificationCode(email, phone);
      setCodeRequested(true);
      setCodeMessage('Verification code sent!');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('temporarily unavailable')) {
        setVerificationSkipped(true);
        setCodeMessage('Phone verification skipped — you can verify later.');
      } else {
        setError(msg || 'Failed to send code');
      }
    } finally {
      setSendingCode(false);
    }
  }, [canSendCode, email, phone]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;
      setSubmitting(true);
      setError(null);
      try {
        await finishSignup(
          email,
          phone,
          verificationSkipped ? undefined : code || undefined,
          password,
          context?.isNewSignup ? firstNameInput.trim() : undefined,
          context?.isNewSignup ? lastNameInput.trim() : undefined,
          completionSource
        );
        await refreshSession();
        onComplete(redirectPath);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to complete signup'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      canSubmit,
      email,
      phone,
      code,
      password,
      verificationSkipped,
      firstNameInput,
      lastNameInput,
      context?.isNewSignup,
      refreshSession,
      onComplete,
      redirectPath,
      completionSource
    ]
  );

  return {
    context,
    loadingContext,
    isNewSignup: context?.isNewSignup ?? false,
    phone,
    setPhone,
    code,
    setCode,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    firstNameInput,
    setFirstNameInput,
    lastNameInput,
    setLastNameInput,
    sendingCode,
    submitting,
    error,
    codeMessage,
    codeRequested,
    verificationSkipped,
    passwordMismatch,
    canSendCode,
    canSubmit,
    handleSendCode,
    handleSubmit
  };
}
